import axios, { AxiosError } from 'axios';

export const api = axios.create({ baseURL: '/api', withCredentials: true });

export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; fields?: Array<{ message: string }> } | undefined;
    if (data?.fields?.length) return data.fields.map(f => f.message).join('. ');
    if (data?.error) return data.error;
  }
  return fallback;
}

export function formatPrice(ore: number, period?: 'month' | 'year'): string {
  if (ore === 0) return 'Free';
  const kr = (ore / 100).toLocaleString('sv-SE', { maximumFractionDigits: 2 });
  return `${kr} kr${period ? (period === 'month' ? '/mo' : '/yr') : ''}`;
}

export function formatDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString('sv-SE') : '—';
}

export type ProductCategory = 'static' | 'wordpress' | 'domain' | 'dns' | 'database';

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'customer' | 'admin';
  phone: string | null;
}

export interface Product {
  slug: string;
  name: string;
  category: ProductCategory;
  tagline: string;
  description: string;
  features: string[];
  priceOre: number;
  currency: string;
  billingPeriod: 'month' | 'year';
  requiresDomain: boolean;
  highlighted: boolean;
  /** priceOre is a "from" price (domain transfers are priced per TLD). */
  priceFrom: boolean;
  /** Domain transfer: needs the domain's auth code at checkout. */
  isTransfer: boolean;
  tld: string | null;
}

/** Price of transferring one domain: CarbonEdge's suggested retail for its TLD × minimum years. */
export interface TransferQuote {
  domain: string;
  tld: string;
  years: number;
  priceOre: number;
  currency: string;
}

export type PaymentMethod = 'swish' | 'stripe' | 'paypal' | 'bitcoin';

export interface Order {
  id: string;
  domain: string | null;
  amountOre: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed' | 'cancelled';
  paidAt: string | null;
  createdAt: string;
  productName?: string;
  productSlug?: string;
}

export interface Payment {
  provider: PaymentMethod;
  status: 'created' | 'pending' | 'paid' | 'declined' | 'error' | 'cancelled';
  amountOre: number;
  errorCode: string | null;
  errorMessage: string | null;
  paidAt: string | null;
  createdAt: string;
}

export type PaymentAction =
  | { type: 'swish_phone'; message: string }
  | { type: 'swish_app'; appUrl: string; token: string }
  | { type: 'redirect'; url: string };

export type ServiceStatus = 'provisioning' | 'active' | 'suspended' | 'failed' | 'cancelled';

export interface SftpInfo {
  host: string;
  port: number;
  username: string;
  protocol?: string | null;
  rootPath?: string | null;
  /** CarbonEdge's browser-based file manager. */
  webFileManagerUrl?: string | null;
}

export interface DatabaseInfo {
  engine: string | null;
  host: string;
  port: number;
  name: string;
  username: string | null;
}

export type TransferStatus = 'pending' | 'awaiting_approval' | 'completed' | 'failed' | 'cancelled';

export interface TransferInfo {
  status: TransferStatus;
  error: string | null;
  startedAt: string;
}

export interface DomainLink {
  at: string;
  applied: string[];
  conflicts: string[];
  error: string | null;
}

export interface Service {
  id: number;
  type: ProductCategory;
  domain: string | null;
  status: ServiceStatus;
  nameservers: string[];
  sftp: SftpInfo | null;
  database: DatabaseInfo | null;
  /** Set for domains transferred in; tracks the transfer while the service is provisioning. */
  transfer: TransferInfo | null;
  /** Hosting on a domain the customer owns through Forget-IT. */
  linkedDomainServiceId: number | null;
  /** How the owned domain was pointed at this hosting. */
  domainLink: DomainLink | null;
  /** Extra databases: the web package they're mounted on. */
  parentServiceId: number | null;
  mountedDatabase: { id: string; name: string; username: string; host: string; port: number; engine: DatabaseEngine } | null;
  lastError: string | null;
  expiresAt: string | null;
  createdAt: string;
  productName?: string;
  productSlug?: string;
}

// ---- CarbonEdge resources (mirrors backend/forgetit/carbonedge/types.ts) ----

/** Optional CarbonEdge features; hidden unless enabled on the server (CARBONEDGE_FEATURES). */
export interface Capabilities {
  dnsEdit: boolean;
  files: boolean;
  wordpressManagement: boolean;
  ssl: boolean;
  php: boolean;
  backupManagement: boolean;
  domainSettings: boolean;
  domainProducts: boolean;
  dnsProducts: boolean;
  domainTransfer: boolean;
}

export interface CarbonEdgeService {
  id: string;
  serviceType: 'wordpress' | 'webhosting' | 'database' | 'container' | string;
  status: 'provisioning' | 'active' | 'suspended' | 'failed' | 'terminated';
  specs: Record<string, string | number | boolean>;
  meta: { nameservers: string[]; sftp: SftpInfo | null; database: DatabaseInfo | null };
}

export const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

export interface DnsRecord {
  id: string;
  type: DnsRecordType;
  name: string;
  content: string;
  ttl: number;
  priority?: number | null;
}

export interface DnsInfo {
  domain: string | null;
  nameservers: string[];
  records: DnsRecord[];
}

export interface EmailAccount {
  id: string | null;
  email: string;
  usage: string | null;
  quotaLimit: string | null;
  quotaMb: number | null;
  status: string;
  /** false = paused: no login or delivery, mail is kept. */
  active: boolean;
  twoFactorEnabled: boolean;
  forwarding: string | null;
}

export interface EmailAlias {
  id: string;
  /** Full address, or "@domain" for the catch-all. */
  source: string;
  destination: string;
  catchAll: boolean;
}

/** MX, SPF, DKIM and DMARC in the zone ("E-posthälsa"). */
export interface MailHealth {
  secured: boolean;
  checks: Array<{ id: 'mx' | 'spf' | 'dkim' | 'dmarc'; ok: boolean; found: string | null; expected: string }>;
}

export interface EmailInfo {
  domain: string | null;
  webmailUrl: string | null;
  serverSettings: {
    imap: { host: string; port: number; ssl: boolean } | null;
    smtp: { host: string; port: number; ssl: boolean } | null;
  };
  accounts: EmailAccount[];
  aliases: EmailAlias[];
}

export interface DomainSettings {
  name: string;
  nameservers: string[];
  autoRenew: boolean;
  locked: boolean;
  dnssec: boolean | null;
  status: string | null;
  expiresAt: string | null;
}

export interface AuthCode {
  authCode: string;
  unlocked: boolean;
  validUntil: string | null;
}

export type DatabaseEngine = 'mysql' | 'postgres';

export interface ManagedDatabase {
  id: string;
  name: string;
  username: string;
  host: string;
  port: number;
  engine: DatabaseEngine;
  size: string | null;
  createdAt: string | null;
  /** Set for paid extra databases; null for the package's own database (WordPress). */
  addon: { serviceId: number; status: ServiceStatus; renewsAt: string | null } | null;
}

/** GET /services/:id/databases */
export interface DatabaseOverview {
  /** phpMyAdmin */
  pmaUrl: string | null;
  databases: ManagedDatabase[];
  /** Paid extra databases being mounted, or that failed to mount. */
  pending: Array<{ serviceId: number; status: ServiceStatus; error: string | null; order: number | null }>;
  /** The extra database add-on. */
  offer: { slug: string; priceOre: number; currency: string; billingPeriod: 'month' | 'year' } | null;
}

/** Database Studio */
export interface DbTables {
  tables: Array<{ name: string; rows: number | null; size: string | null }>;
  totalSize: string | null;
}

export interface DbRows {
  columns: string[];
  rows: unknown[][];
  page: number | null;
  limit: number | null;
  total: number | null;
}

export interface DbQueryResult extends DbRows {
  affectedRows: number | null;
  message: string | null;
}

export interface FileEntry {
  name: string;
  path: string;
  type: 'file' | 'dir';
  size: number | null;
  modifiedAt: string;
}

export interface WordPressInfo {
  installed: boolean;
  version: string | null;
  latestVersion: string | null;
  needsCoreUpdate: boolean;
  autoUpdate: boolean;
  adminUrl: string | null;
  plugins: Array<{ slug: string; name: string; version: string; active: boolean; updateAvailable: boolean }>;
  themes: Array<{ slug: string; name: string; version: string; active: boolean }>;
  maintenanceMode: boolean;
}

export interface SslCertificate {
  status: 'none' | 'pending' | 'active' | 'failed';
  issuer: string | null;
  domains: string[];
  expiresAt: string | null;
  autoRenew: boolean;
  forceHttps: boolean;
}

export interface PhpInfo {
  currentVersion: string;
  availableVersions: string[];
  memoryLimit: string | null;
  opcacheEnabled: boolean | null;
}

export interface Backup {
  id: string;
  label: string | null;
  createdAt: string;
  sizeMb: number | null;
  kind: 'automatic' | 'manual';
  status: 'running' | 'completed' | 'failed';
}

export interface BackupInfo {
  frequency: string | null;
  retentionDays: number | null;
  status: string | null;
  backups: Backup[];
}

export interface Credentials {
  username: string;
  password: string;
}

export interface ServiceDetail {
  service: Service;
  /** Set when an admin views someone else's service. */
  owner: { id: number; name: string; email: string } | null;
  /** For domain services: the hosting package using this domain. */
  linkedHosting: { id: number; status: ServiceStatus; productName: string | null } | null;
  /** For hosting services: the owned domain this hosting uses. */
  linkedDomain: { id: number; domain: string | null; status: ServiceStatus } | null;
  /** For extra databases: the web package whose Database tab manages them. */
  parentService: { id: number; domain: string | null; type: ProductCategory; status: ServiceStatus } | null;
  /** Live CarbonEdge view of an active/suspended service. */
  carbonEdge: CarbonEdgeService | null;
  capabilities: Capabilities;
  /** CarbonEdge web proxy IPs to point @ and www at (sites only). */
  webIps: string[];
}

// ---- Admin ----

export interface AdminStats {
  customers: number;
  activeServices: number;
  attentionServices: number;
  revenueThisMonthOre: number;
}

export interface AdminCustomerRow {
  id: number;
  email: string;
  name: string;
  role: 'customer' | 'admin';
  phone: string | null;
  carbonEdgeCustomerId: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  services: number;
  activeServices: number;
  attentionServices: number;
  orders: number;
}

export interface AdminCustomerList {
  customers: AdminCustomerRow[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AdminOrder extends Order {
  paymentStatus: Payment['status'] | null;
  paymentProvider: PaymentMethod | null;
  paymentAttempts: number;
}

export interface AdminCustomerDetail {
  customer: Omit<AdminCustomerRow, 'services' | 'activeServices' | 'attentionServices' | 'orders'>;
  services: Service[];
  orders: AdminOrder[];
}

// ---- CV generator (mirrors backend/forgetit/resume-helper/skills.ts) ----

export interface CvSkill {
  id: string;
  name: string;
  category: string;
  /** The first paragraph follows "<name> – " in the PDF. */
  paragraphs: string[];
}
